import clsx from 'clsx';
import { useState } from 'react';
import { usePopper } from 'react-popper';
import ReactDOM from 'react-dom';

// Types
import type { ReactNode, Dispatch, SetStateAction } from 'react';
import type { CustomPopoverProps } from '@/components/composite/Popover/CustomPopover';

// Utils
import useToggle from '@/utils/hooks/utils/useToggle';

// Components
import Icon from '@/components/base/Icon';
import CustomPopover from '@/components/composite/Popover/CustomPopover';

export type DefaultPopoverItemType = {
    icon?: string;
    isActive?: boolean;
    click: () => void;
    content: ReactNode;
};

export type DefaultPopoverProps<TReferenceElement extends Element> = Omit<
    CustomPopoverProps,
    'popperElement' | 'setPopperElement' | 'toggle' | 'styles' | 'attributes' | 'children'
> & {
    children: (arg: {
        isPopoverOpen?: boolean;
        setReferenceElement: Dispatch<SetStateAction<TReferenceElement>>;
        togglePopover: () => void;
    }) => ReactNode;
    popoverItems: DefaultPopoverItemType[];
};

function DefaultPopover<TReferenceElement extends Element>({
    children,
    popoverItems,
    ...restProps
}: DefaultPopoverProps<TReferenceElement>) {
    const { isOpen, toggle } = useToggle();
    const [referenceElement, setReferenceElement] = useState<TReferenceElement>(null);

    const [popperElement, setPopperElement] = useState<Parameters<typeof usePopper>[1]>(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 10]
                }
            }
        ]
    });

    const popperStylesWithZIndex = {
        popper: { zIndex: 100, ...styles.popper }
    };

    return (
        <>
            {children({ setReferenceElement, isPopoverOpen: isOpen, togglePopover: toggle })}
            {isOpen
                ? ReactDOM.createPortal(
                      <CustomPopover
                          popperElement={popperElement}
                          setPopperElement={setPopperElement}
                          toggle={toggle}
                          styles={popperStylesWithZIndex}
                          attributes={attributes}
                          {...restProps}>
                          <ul className="rounded-m bg-white p-1 dark:bg-icoDarkMode-anthracite">
                              {popoverItems.map((item, index) => {
                                  return (
                                      <li key={index}>
                                          <button
                                              onClick={item.click}
                                              className={clsx(
                                                  'flex w-full items-center space-x-2 rounded-sm p-2 text-xs',
                                                  'hover:bg-icoBlue-100 hover:text-icoBlue focus:ring-inset focus:ring-offset-0 dark:hover:bg-icoBlue/20 hover:dark:text-icoBlue',
                                                  {
                                                      'text-icoGray-500 dark:text-icoDarkMode-wolf':
                                                          !item?.isActive,
                                                      'text-icoBlue': item?.isActive
                                                  }
                                              )}>
                                              {item?.icon ? (
                                                  <Icon icon={item.icon} className="text-sm" />
                                              ) : null}
                                              <span>{item.content}</span>
                                          </button>
                                      </li>
                                  );
                              })}
                          </ul>
                      </CustomPopover>,
                      document.body
                  )
                : null}
        </>
    );
}

export default DefaultPopover;
